import type { ChatMessage } from "./useBuilder";

/**
 * Per-page persistence for the Nara conversation thread.
 *
 * The Make builder keeps the conversation (`messages`) in memory; this module
 * mirrors it to localStorage keyed by page id so the thread survives switching
 * pages AND a full reload — "chat history persists for each edited page". It is
 * deliberately pure + dependency-free so it is trivially unit-testable and never
 * throws (quota / disabled storage / corrupt JSON all degrade to a no-op / []).
 *
 * Scope: local to the browser (same convention as `make.activePage.${projectId}`
 * in useBuilder and the Dashboard widgets). Cross-device sync would need a
 * backend conversation store; that is out of scope here.
 */

const PREFIX = "make.chat.";
/** Hard cap so a long-lived conversation can't grow localStorage unbounded. */
export const MAX_MESSAGES = 200;

function keyFor(pageId: string): string {
  return PREFIX + pageId;
}

function storage(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    // Accessing localStorage can throw in some sandboxed/private contexts.
    return null;
  }
}

function isChatMessage(v: unknown): v is ChatMessage {
  if (!v || typeof v !== "object") return false;
  const m = v as Record<string, unknown>;
  return (
    (m.role === "user" || m.role === "assistant") &&
    typeof m.content === "string" &&
    typeof m.key === "string"
  );
}

/**
 * Load a page's persisted conversation (oldest first). Returns `[]` when there
 * is none, the page id is missing, or the stored value is absent/unparseable.
 * Filters out any malformed entries. Never throws.
 */
export function loadChatHistory(pageId: string | undefined | null): ChatMessage[] {
  const ls = storage();
  if (!pageId || !ls) return [];
  try {
    const raw = ls.getItem(keyFor(pageId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isChatMessage);
  } catch {
    return [];
  }
}

/**
 * Persist a page's conversation (capped to the most recent MAX_MESSAGES).
 * An empty array removes the key. Never throws.
 */
export function saveChatHistory(
  pageId: string | undefined | null,
  messages: ChatMessage[],
): void {
  const ls = storage();
  if (!pageId || !ls) return;
  try {
    if (!messages.length) {
      ls.removeItem(keyFor(pageId));
      return;
    }
    const capped = messages.length > MAX_MESSAGES ? messages.slice(-MAX_MESSAGES) : messages;
    ls.setItem(keyFor(pageId), JSON.stringify(capped));
  } catch {
    /* quota exceeded / storage disabled — degrade silently */
  }
}

/** Remove a page's persisted conversation (used by reset / "Start over"). */
export function clearChatHistory(pageId: string | undefined | null): void {
  const ls = storage();
  if (!pageId || !ls) return;
  try {
    ls.removeItem(keyFor(pageId));
  } catch {
    /* ignore */
  }
}
